import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import type { Category, Insight } from '../types/api';
import { CtaLink, Eyebrow, MediaCard, Reveal, RichHtml, SafeImage, Section } from '../components/ui';
import { SubscribeCard } from '../components/insights';
import { useNavData } from '../components/layout/useNavData';
import { paragraphs, summary } from '../utils/text';

const readingMinutes = (i: Insight) => {
  const words = [i.description, i.content ?? ''].join(' ').split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};
const longDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function BlogDetail() {
  const { id } = useParams<{ id: string }>();
  const [insight, setInsight] = useState<Insight | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [related, setRelated] = useState<Insight[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const { services } = useNavData();

  useEffect(() => {
    if (!id) { setStatus('error'); return; }
    let alive = true;
    setStatus('loading');
    (async () => {
      try {
        const data = await apiService.getInsight(id);
        if (!alive) return;
        setInsight(data);
        setStatus('ok');
        if (data.category_id) apiService.getCategory(data.category_id).then((c) => alive && setCategory(c)).catch(() => {});
        else setCategory(null);
        apiService.getInsights().then((all) => alive && setRelated(all.filter((x) => x.id !== data.id).slice(0, 3))).catch(() => {});
      } catch {
        if (alive) setStatus('error');
      }
    })();
    return () => { alive = false; };
  }, [id]);

  if (status === 'loading') {
    return <div aria-busy className="animate-pulse"><div className="h-[20rem] bg-secondary-200 md:h-[38rem]" /><div className="site-container mt-12 space-y-4"><div className="h-4 w-40 bg-secondary-200" /><div className="h-16 w-3/4 bg-secondary-200" /></div></div>;
  }

  if (status === 'error' || !insight) {
    return (
      <Section eyebrow="Actualités" heading="Actualité introuvable" background="white">
        <p className="mb-8 max-w-xl text-lg text-ink">Cette actualité n'existe pas ou n'est plus disponible.</p>
        <CtaLink to="/blog">Retour aux actualités</CtaLink>
      </Section>
    );
  }

  const [lead, ...rest] = paragraphs(insight.description);
  // Texte complet saisi dans l'admin (texte riche); à défaut, texte brut en paragraphes
  const body = paragraphs(insight.content);
  const gallery = (insight.insight_galery ?? []).filter((g) => g.gallery?.url && !/placeholder\.com/i.test(g.gallery.url));
  const minutes = readingMinutes(insight);

  return (
    <article className="text-black">
      {insight.image?.url && (
        <div className="h-[26rem] overflow-hidden bg-secondary-200 sm:h-[30rem] md:h-[56rem]">
          <img src={insight.image.url} alt="" className="h-full w-full animate-fade-in object-cover" />
        </div>
      )}

      {/* Pas de balise <header>: l'ancien CSS la force en position fixe */}
      <div className="site-container pb-10 pt-12 md:pb-14 md:pt-16">
        <Eyebrow className="!font-normal">{category?.title ?? 'Actualité'}</Eyebrow>
        <h1 className="mt-5 max-w-5xl text-black font-display text-[2.5rem] font-bold normal-case leading-[1.05] tracking-normal md:text-[4.5rem]">{insight.title}</h1>
        <p className="mt-8 text-[0.9375rem] text-ink">Actualité <span aria-hidden className="mx-2">|</span> {minutes} min de lecture</p>
        <p className="mt-2 text-xs text-ink">Publié le {longDate(insight.created_at)}</p>
      </div>

      <div className="site-container grid gap-14 pb-20 lg:grid-cols-[minmax(0,41rem)_1fr] lg:gap-x-24 lg:pb-28">
        <div>
          {lead && !insight.descriptionHtml && <Reveal><p className="font-display text-[1.25rem] font-normal leading-[1.4] md:text-[1.5rem]">{lead}</p></Reveal>}
          {insight.descriptionHtml ? <RichHtml html={insight.descriptionHtml} className="mt-8" /> : rest.length > 0 && <div className="mt-8 space-y-5 text-[1.0625rem] leading-relaxed">{rest.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}</div>}

          {insight.contentHtml ? (
            <Reveal className="mt-14"><RichHtml html={insight.contentHtml} /></Reveal>
          ) : (
            body.length > 0 && <Reveal className="mt-14"><div className="space-y-5 text-[1.0625rem] leading-relaxed">{body.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}</div></Reveal>
          )}

          {gallery.map((g) => (
            <Reveal key={g.id} className="mt-14">
              <SafeImage src={g.gallery!.url} alt={g.gallery!.legende || insight.title} caption={g.gallery!.legende} />
            </Reveal>
          ))}

          <div className="mt-16"><CtaLink to="/blog">Toutes les actualités</CtaLink></div>
        </div>

        <aside className="space-y-10 lg:sticky lg:top-28 lg:self-start">
          <SubscribeCard />
          {services.length > 0 && (
            <div>
              <div className="border-b border-line pb-3 text-xs font-semibold uppercase tracking-[0.0625rem] text-ink">Nos services</div>
              <ul className="mt-2">
                {services.slice(0, 6).map((s) => (
                  <li key={s.id}>
                    <Link to={`/services/${s.id}`} className="group flex items-start justify-between gap-4 border-b border-line py-3.5">
                      <span><span className="block font-display text-lg font-semibold leading-tight transition-colors group-hover:text-brand-dark">{s.name}</span><span className="mt-1 block text-sm text-ink">{summary(s.description, 70)}</span></span>
                      <span aria-hidden className="mt-2 h-px w-5 shrink-0 bg-brand transition-all duration-300 group-hover:w-9" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {related.length > 0 && (
        <Section eyebrow="À lire aussi" heading="D'autres actualités" background="surface">
          <div className="grid gap-8 md:grid-cols-3">
            {related.map((r, i) => <MediaCard key={r.id} to={`/blog/${r.id}`} image={r.image?.url} title={r.title} eyebrow="Actualité" text={summary(r.description, 120)} delay={i * 0.1} />)}
          </div>
        </Section>
      )}
    </article>
  );
}
